/**
 * inbound (입고) orval 래퍼
 *
 * 기존 SDK와 동일한 인터페이스를 제공한다. customInstance를 사용하여
 * 기존 코드에서 import 경로만 바꾸면 동작한다.
 */
import { customInstance } from "../custom-instance";
import { DefaultApprovalStatusSchema, MaterialManagerSchema, PurchaseManagerSchema, } from "./default-schemes";
import { PRODUCT_TYPE } from "@erp/shared/types/product.types";
import { validateArrayWithSchema, validateWithSchema, } from "@erp/shared/utils/https/zod-validation";
import { z } from "zod";
// ── URL 매핑 ──
const INBOUND_URLS = {
    commercial: {
        confirm: "/inbound-confirm/commercial",
        purchase: "/inbound-confirm/commercial/purchase",
        products: (confirmId) => `/inbound-confirm/commercial/${confirmId}/products`,
        productAll: "/stock-transaction/commercial/inbound",
    },
    research: {
        confirm: "/inbound-confirm/research",
        purchase: "/inbound-confirm/research/purchase",
        products: (confirmId) => `/inbound-confirm/research/${confirmId}/products`,
        productAll: "/stock-transaction/research/inbound",
    },
};
// ── 스키마 & 타입 (InboundConfirm) ──
export const GetInboundConfirmResponseSchema = z
    .object({
    ...DefaultApprovalStatusSchema.shape,
    ...MaterialManagerSchema.shape,
    ...PurchaseManagerSchema.shape,
    id: z.number(),
    projectCode: z.string().nullable(),
    addData: z.string().nullable(),
    inboundDate: z.string().datetime().nullable(),
    abnormalReason: z.string().nullable(),
    note: z.string().nullable(),
    receivingInspectionFile: z.string().nullable(),
    inspectionReportFile: z.string().nullable(),
    etcFiles: z.array(z.string()).nullable(),
    approvalDocumentId: z.number().nullable().optional(),
    expenditureResolutionId: z.number().nullable().optional(),
    inspectionDescription: z.string().nullable().optional(),
    inspectionPhotos: z.array(z.string()).nullable().optional(),
    hasRemainingPayment: z.number().nullable().optional(),
    inspectionDate: z.string().nullable().optional(),
})
    .strict();
// ── 스키마 & 타입 (InboundProducts) ──
export const GetInboundProductResponseSchema = z
    .object({
    ...DefaultApprovalStatusSchema.shape,
    ...MaterialManagerSchema.shape,
    ...PurchaseManagerSchema.shape,
    id: z.coerce.number(),
    type: z.string().nullable(),
    productCode: z.string().nullable(),
    manufacturer: z
        .object({ id: z.coerce.number(), name: z.string() })
        .nullable(),
    classification: z
        .object({ id: z.coerce.number(), name: z.string() })
        .nullable(),
    name: z.string().nullable(),
    spec: z.string().nullable(),
    model: z.string().nullable(),
    spot: z.object({ id: z.coerce.number(), name: z.string() }).nullable(),
    num: z.number(),
    quantityUnit: z.string().nullable(),
    unitPrice: z.number(),
    inboundDate: z.string().datetime().nullable(),
    abnormalReason: z.string().nullable(),
    receivingInspectionFile: z.string().nullable(),
    inspectionReportFile: z.string().nullable(),
    etcFiles: z.array(z.string()).nullable(),
    note: z.string().nullable(),
    addData: z.string().nullable(),
    projectCode: z.string().nullable(),
})
    .strict();
// ── 스키마 & 타입 (InboundProductAll) ──
export const GetInboundProductAllResponseSchema = z
    .object({
    id: z.number(),
    spotId: z.number(),
    num: z.number(),
    unitPrice: z.number(),
    spot: z.string(),
    productId: z.number().nullable(),
    type: z.string(),
    productCode: z.string(),
    name: z.string(),
    spec: z.string(),
    model: z.string(),
    manufacturerId: z.coerce.number(),
    manufacturer: z.string(),
    classificationId: z.coerce.number(),
    classification: z.string(),
    orderId: z.coerce.number().nullable(),
    inboundConfirmId: z.coerce.number().nullable(),
    inboundDate: z.string().nullable(),
    purchaseManager: z.coerce.number().nullable(),
    purchaseManagerId: z.string().nullable(),
    purchaseManagerName: z.string().nullable(),
    materialManager: z.coerce.number().nullable(),
    materialManagerId: z.string().nullable(),
    materialManagerName: z.string().nullable(),
    abnormalReason: z.string().nullable(),
    receivingInspectionFile: z.string().nullable(),
    inspectionReportFile: z.string().nullable(),
    etcFiles: z.array(z.string()).nullable(),
    note: z.string().nullable(),
    addData: z.string().nullable(),
    projectId: z.coerce.number().nullable(),
    projectCode: z.string().nullable(),
    createdTime: z.string().nullable(),
    modifiedTime: z.string().nullable(),
    modifier: z.coerce.number().nullable(),
    approvalPhase: z.string().nullable(),
    creater: z.coerce.number().nullable(),
    checker: z.coerce.number().nullable(),
    checkedDate: z.string().nullable(),
    approver: z.coerce.number().nullable(),
    approvedDate: z.string().nullable(),
    rejecter: z.coerce.number().nullable(),
    rejectedDate: z.string().nullable(),
    rejectReason: z.string().nullable(),
    createrId: z.string().nullable(),
    createrName: z.string().nullable(),
    checkerId: z.string().nullable(),
    checkerName: z.string().nullable(),
    approverId: z.string().nullable(),
    approverName: z.string().nullable(),
    rejecterId: z.string().nullable(),
    rejecterName: z.string().nullable(),
})
    .strict();
export const RequestedPurchaseProductSchema = z
    .object({
    id: z.number(),
    approvalPhase: z.string(),
    num: z.number(),
    inboundDate: z.string(),
    spot: z.string(),
    spotId: z.number(),
    unitPrice: z.number(),
    conditions: z.string().nullable(),
})
    .strict();
export const GetPurchaseForInboundResponseSchema = z
    .object({
    id: z.number(),
    orderId: z.number(),
    projectId: z.number(),
    productId: z.number().nullable(),
    projectCode: z.string(),
    type: z.nativeEnum(PRODUCT_TYPE).nullable(),
    classification: z.string().nullable(),
    classificationId: z.number().nullable(),
    productCode: z.string().nullable(),
    name: z.string().nullable(),
    purchaseNum: z.number(),
    spec: z.string().nullable(),
    model: z.string().nullable(),
    manufacturer: z.string().nullable(),
    manufacturerId: z.number().nullable(),
    unitPrice: z.number().nullable(),
    inbounds: z.array(RequestedPurchaseProductSchema),
    completeNum: z.number(),
    pendingNum: z.number(),
    leftNum: z.number(),
})
    .strict();
// ── API 함수 ──
function createGetInboundConfirm(type) {
    return async (params) => {
        const data = await customInstance({
            url: INBOUND_URLS[type].confirm,
            method: "GET",
            params,
        });
        return validateArrayWithSchema(GetInboundConfirmResponseSchema, data);
    };
}
export const getCommercialInboundConfirm = createGetInboundConfirm("commercial");
export const getResearchInboundConfirm = createGetInboundConfirm("research");
function createPostInboundConfirm(type) {
    return async (body) => {
        return customInstance({
            url: INBOUND_URLS[type].confirm,
            method: "POST",
            headers: { "Content-Type": "application/json" },
            data: body,
        });
    };
}
export const postCommercialInboundConfirm = createPostInboundConfirm("commercial");
export const postResearchInboundConfirm = createPostInboundConfirm("research");
function createPatchInboundApprove(type) {
    return async ({ pathParams, body }) => {
        return customInstance({
            url: `${INBOUND_URLS[type].confirm}/${pathParams.segment}/${pathParams.inboundId}`,
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            data: body,
        });
    };
}
export const patchCommercialInboundApprove = createPatchInboundApprove("commercial");
export const patchResearchInboundApprove = createPatchInboundApprove("research");
function createGetInboundProducts(type) {
    return async (inboundId) => {
        const data = await customInstance({
            url: INBOUND_URLS[type].products(inboundId),
            method: "GET",
        });
        return validateArrayWithSchema(GetInboundProductResponseSchema, data);
    };
}
export const getCommercialInboundProducts = createGetInboundProducts("commercial");
export const getResearchInboundProducts = createGetInboundProducts("research");
function createGetInboundProductAll(type) {
    return async (params) => {
        const data = await customInstance({
            url: INBOUND_URLS[type].productAll,
            method: "GET",
            params: { ...params, noInitial: true },
        });
        return validateArrayWithSchema(GetInboundProductAllResponseSchema, data);
    };
}
export const getCommercialInboundProductAll = createGetInboundProductAll("commercial");
export const getResearchInboundProductAll = createGetInboundProductAll("research");
function createGetPurchaseForInbound(type) {
    return async (params) => {
        const data = await customInstance({
            url: INBOUND_URLS[type].purchase,
            method: "GET",
            params,
        });
        return validateArrayWithSchema(GetPurchaseForInboundResponseSchema, data);
    };
}
export const getCommercialPurchaseForInbound = createGetPurchaseForInbound("commercial");
export const getResearchPurchaseForInbound = createGetPurchaseForInbound("research");
export async function getInboundConfirmDetail(id) {
    const data = await customInstance({
        url: `/inbound-confirm/commercial/${id}`,
        method: "GET",
    });
    return validateWithSchema(GetInboundConfirmResponseSchema, data);
}
// ── SDK 객체 ──
export const inboundApi = {
    getCommercialInboundConfirm,
    getResearchInboundConfirm,
    postCommercialInboundConfirm,
    postResearchInboundConfirm,
    patchCommercialInboundApprove,
    patchResearchInboundApprove,
    getCommercialInboundProducts,
    getResearchInboundProducts,
    getCommercialInboundProductAll,
    getResearchInboundProductAll,
    getCommercialPurchaseForInbound,
    getResearchPurchaseForInbound,
    getInboundConfirmDetail,
};
